import { formatMin, MINUTES_PER_DAY, normalizeBlockTitle, parseWallTimeText, type Span } from '@wiztime/core';

/**
 * Pure model of the picker's field drafts (2026-10b §5.4): every field has a baseline value
 * (its value when the picker opened or was last saved); "modified" means the current value no
 * longer equals the baseline — clearing to empty included. The drafts live in `dayUiStore`,
 * not in component state, so the single commit point `flushPicker` can reach them.
 */

/** The values the picker opened with (or last saved), plus its target. */
export interface PickerBaseline {
  /** Ids of the records a commit writes to; empty in the create context. */
  readonly blockIds: readonly string[];
  readonly date: string;
  readonly startMin: number;
  readonly endMin: number;
  /** Stored title ('' when the block has none). */
  readonly title: string;
  /** Stored note ('' when the block has none). */
  readonly note: string;
  readonly dayStartMin: number;
  /** Time step in minutes (the arrow keys of a TimeField move along this grid). */
  readonly stepMin: number;
  /** Start and end may be edited: a single block, or the create context. */
  readonly spanEditable: boolean;
  readonly isDraft: boolean;
}

export interface PickerFields {
  readonly baseline: PickerBaseline;
  readonly startText: string;
  readonly endText: string;
  readonly title: string;
  readonly note: string;
  /** Create context: the "nothing saved yet" notice was shown; the next dismissal abandons. */
  readonly noticeShown: boolean;
  /** Bumped on every notice, to retrigger the shake animation (2026-10b §5.4). */
  readonly noticeCount: number;
}

export const PickerTextField = {
  Start: 'startText',
  End: 'endText',
  Title: 'title',
  Note: 'note',
} as const;
export type PickerTextField = (typeof PickerTextField)[keyof typeof PickerTextField];

export function initPickerFields(baseline: PickerBaseline): PickerFields {
  return {
    baseline,
    startText: formatMin(baseline.startMin),
    endText: formatMin(baseline.endMin),
    title: baseline.title,
    note: baseline.note,
    noticeShown: false,
    noticeCount: 0,
  };
}

/** The text a field falls back to on Esc (its baseline value, §5.4). */
export function baselineTextOf(fields: PickerFields, field: PickerTextField): string {
  switch (field) {
    case PickerTextField.Start:
      return formatMin(fields.baseline.startMin);
    case PickerTextField.End:
      return formatMin(fields.baseline.endMin);
    case PickerTextField.Title:
      return fields.baseline.title;
    case PickerTextField.Note:
      return fields.baseline.note;
  }
}

/**
 * Absolute start minute of the start field's text: a wall time before the personal-day start
 * is the small hours of the next day (2026-10b §3.8). Null when the text is not a time.
 */
export function parsePickerStart(text: string, dayStartMin: number): number | null {
  const wall = parseWallTimeText(text);
  if (wall === null) return null;
  return wall < dayStartMin ? wall + MINUTES_PER_DAY : wall;
}

/**
 * Absolute end minute of the end field's text: the earliest value after `startMin` (§3.8).
 * May exceed the personal day; the save-time check reports that. Null when not a time.
 */
export function parsePickerEnd(text: string, startMin: number): number | null {
  const wall = parseWallTimeText(text);
  if (wall === null) return null;
  let end = wall;
  while (end <= startMin) end += MINUTES_PER_DAY;
  return end;
}

export const PickerTimeError = {
  /** One of the fields does not parse as a time. */
  Format: 'format',
  /** The end would land past the end of the personal day. */
  Range: 'range',
} as const;
export type PickerTimeError = (typeof PickerTimeError)[keyof typeof PickerTimeError];

/** What the picker would write (only the changed, valid fields) plus the dirtiness verdicts. */
export interface PickerDiff {
  readonly patch: {
    readonly title?: string | null;
    readonly note?: string | null;
    readonly span?: Span;
  };
  /** Any field is modified (an invalid time text counts as modified). */
  readonly dirty: boolean;
  readonly titleDirty: boolean;
  readonly noteDirty: boolean;
  readonly timeDirty: boolean;
  readonly timeError: PickerTimeError | null;
  /** The parsed span whenever both time fields parse ('format' error absent). */
  readonly span: Span | null;
}

export function pickerDiff(fields: PickerFields): PickerDiff {
  const base = fields.baseline;
  const patch: { title?: string | null; note?: string | null; span?: Span } = {};

  const title = normalizeBlockTitle(fields.title);
  const titleDirty = title !== normalizeBlockTitle(base.title);
  if (titleDirty) patch.title = title;

  const trimmedNote = fields.note.trim();
  const note = trimmedNote === '' ? null : trimmedNote;
  const baseNote = base.note === '' ? null : base.note;
  const noteDirty = note !== baseNote;
  if (noteDirty) patch.note = note;

  const start = parsePickerStart(fields.startText, base.dayStartMin);
  const effectiveStart = start ?? base.startMin;
  const end = parsePickerEnd(fields.endText, effectiveStart);
  let timeError: PickerTimeError | null = null;
  if (start === null || end === null) timeError = PickerTimeError.Format;
  else if (end > base.dayStartMin + MINUTES_PER_DAY) timeError = PickerTimeError.Range;
  const span = start !== null && end !== null ? { startMin: start, endMin: end } : null;
  const timeDirty = base.spanEditable
    ? span === null
      ? fields.startText !== formatMin(base.startMin) || fields.endText !== formatMin(base.endMin)
      : span.startMin !== base.startMin || span.endMin !== base.endMin
    : false;
  if (base.spanEditable && timeDirty && timeError === null && span !== null) patch.span = span;

  return {
    patch,
    dirty: titleDirty || noteDirty || timeDirty,
    titleDirty,
    noteDirty,
    timeDirty,
    timeError,
    span,
  };
}

/** Whether the picker has unsaved modifications (defers the worker-update reload, §5.4). */
export function pickerFieldsDirty(fields: PickerFields | null): boolean {
  return fields !== null && pickerDiff(fields).dirty;
}
